/**
 * Timezone-aware date utilities
 * Fixes the core issue where app used UTC dates instead of user's local dates
 */

/**
 * Get today's date in user's local timezone as YYYY-MM-DD string
 * This replaces new Date().toISOString().split('T')[0] throughout the app
 */
export function getTodayLocalDateString(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const day = today.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get a specific date in user's local timezone as YYYY-MM-DD string
 * @param date - Date object to convert
 */
export function getLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Create a Date object from a local date string (YYYY-MM-DD)
 * This ensures the date is interpreted in the user's local timezone
 * @param dateString - Date string in YYYY-MM-DD format
 */
export function createLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
}

/**
 * Check if a date string represents today in user's local timezone
 * @param dateString - Date string in YYYY-MM-DD format
 */
export function isToday(dateString: string): boolean {
  return dateString === getTodayLocalDateString();
}

/**
 * Check if a date string represents yesterday in user's local timezone
 * @param dateString - Date string in YYYY-MM-DD format
 */
export function isYesterday(dateString: string): boolean {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return dateString === getLocalDateString(yesterday);
}

/**
 * Get date string for N days ago in user's local timezone
 * @param daysAgo - Number of days to go back
 */
export function getDaysAgoDateString(daysAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return getLocalDateString(date);
}

/**
 * Get date string for N days from now in user's local timezone
 * @param daysFromNow - Number of days to go forward
 */
export function getDaysFromNowDateString(daysFromNow: number): string {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return getLocalDateString(date);
}

/**
 * Format a date string for display
 * @param dateString - Date string in YYYY-MM-DD format
 * @param options - Intl.DateTimeFormat options
 */
export function formatDateString(
  dateString: string, 
  options: Intl.DateTimeFormatOptions = { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric' 
  }
): string {
  const date = createLocalDate(dateString);
  return date.toLocaleDateString(undefined, options);
}

/**
 * Get an array of the last N days as date strings in user's local timezone
 * @param days - Number of days to include (including today)
 */
export function getLastNDays(days: number): string[] {
  const result: string[] = [];
  for (let i = days - 1; i >= 0; i--) {
    result.push(getDaysAgoDateString(i));
  }
  return result;
}

/**
 * Check if we need to migrate existing UTC-based dates to local dates
 * This is a one-time migration helper
 */
export function detectTimezoneOffset(): number {
  return new Date().getTimezoneOffset(); // minutes
}

/**
 * Convert a UTC date string to local date string (for migration)
 * @param utcDateString - UTC date string
 * @param timezoneOffsetMinutes - User's timezone offset in minutes
 */
export function convertUtcToLocalDateString(
  utcDateString: string, 
  timezoneOffsetMinutes: number
): string {
  const utcDate = new Date(utcDateString + 'T00:00:00Z');
  const localDate = new Date(utcDate.getTime() - (timezoneOffsetMinutes * 60000));
  return getLocalDateString(localDate);
} 